import React, { useEffect, useState } from 'react';
import { LogIn } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Landing-hero variant — "personal / own-recordings" experiment (2026-08-19).
 *
 * The reader is a hobbyist making their own sound (scratches, synth jams,
 * piano practice, voice memos) and wants to carry it around and revisit
 * it. NOT the "share a mix with the band/collaborator" lane. Copy is scoped
 * accordingly — no sharing/handoff verbs.
 *
 * Why this component exists (and not a Hero.tsx edit):
 *   - The current Hero.tsx runs the 3-tier + rotating typewriter — that's
 *     working, has real telemetry, and is being A/B'd on its own dimensions.
 *     Adding a photo hero as a variant of THIS component is opt-in and
 *     reversible: delete this file, remove the FLAGS.HERO_PERSONAL entry,
 *     remove the two lines in Home.tsx. No coupling to Hero's internals.
 *   - Gated behind `FLAGS.HERO_PERSONAL` OR `?hero=personal` URL param. Off
 *     by default in prod — Glenn previews via URL param before deciding to
 *     flip the flag on for real. When both are off, this component is not
 *     mounted at all (Home.tsx short-circuits the render), so the anon
 *     bundle doesn't pay for it beyond the code-split cost.
 *
 * Visual language — LOAD-BEARING:
 *   - Aspect ratio 16:9 (`aspect-video`) reserved on the outer wrapper. The
 *     source image is 1672x941 (ratio 1.777 ≈ 16:9). Reserving the box
 *     means zero CLS regardless of image load timing — Glenn hates layout
 *     shift. Do NOT swap this for intrinsic sizing.
 *   - Image is loaded eagerly with fetchpriority="high" — it's above the
 *     fold on the anon landing and is the LCP element when this variant is
 *     active. A lazy hero image would score badly in Core Web Vitals.
 *   - Copy overlay sits INSIDE the scene, not stamped on top: a bottom-up
 *     gradient veil (black/70 → transparent) grounds the type in the
 *     lower-left third of the frame. That's the cinematic pattern (movie
 *     posters, print editorial) — the image carries the emotion, the type
 *     anchors it, they belong together.
 *   - Type treatment mirrors Hero.tsx's h1 (text-3xl/text-5xl, font-bold,
 *     tracking-tight, leading-tight) so the two heroes read as siblings
 *     when both are visible in the flow.
 *
 * Animation:
 *   - CSS-only opacity fade on mount (0 → 1 over 700ms). No y-translate,
 *     no scale, no bounce. Matches the existing hero's FADE_MS crossfade
 *     cadence so the two feel like they belong to the same design system.
 *   - `prefers-reduced-motion`: opacity starts at 1 (no fade). No motion
 *     to gate — the image sitting there is not motion.
 *   - No infinite loops, no pulses, no shimmer. The image is already
 *     atmospheric; loud animation on top would fight it.
 *
 * Accessibility:
 *   - `alt` text describes what's in the image AND why it's here, so
 *     screen readers get the same emotional beat as sighted users.
 *   - Overlay copy is real DOM (h1 + p), not baked into the image, so
 *     it's readable, translatable, and indexable.
 */

const HeroPersonal: React.FC = () => {
  const { signIn } = useAuth();

  // Fade-in on mount. CSS transition + one state flip = 1 render, no rAF
  // dance. Starts opacity 0 on mount then flips to 1 in the next effect
  // tick, letting the transition catch the change.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    // requestAnimationFrame ensures the initial paint lands at opacity 0
    // before the transition target is set. Without this, some browsers
    // will collapse the state flip into the initial paint and skip the
    // transition (visible: true from first paint = no fade).
    const raf = window.requestAnimationFrame(() => setVisible(true));
    return () => window.cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      // Mobile: portrait 4:5 (roughly 2× the height of the shipped 16:9 on
      // a 375px screen — ~419px vs ~188px). The 16:9 landscape aspect was
      // squashing the headline to unreadable and turning the image into
      // decorative wallpaper. Center-cropped 4:5 gives the copy breathing
      // room AND lets the image carry weight instead of a thin band.
      // md+: reverts to aspect-video since desktop viewports have the
      // horizontal room for the source's native 16:9 to shine.
      className="relative w-full aspect-[4/5] md:aspect-video overflow-hidden rounded-2xl border shadow-lg"
      style={{
        borderColor: 'rgba(255, 255, 255, 0.10)',
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
        opacity: visible ? 1 : 0,
        transition: 'opacity 700ms ease-in-out',
      }}
    >
      <img
        src="/hero-personal.jpg"
        alt="A person on a subway train wearing over-ear headphones, listening to their own recording on pastewaves"
        // Above the fold on the anon landing when this variant is active —
        // must not be lazy or async. This IS the LCP element.
        loading="eager"
        // @ts-expect-error — fetchpriority is a valid HTML attribute (2024+)
        // but not yet in React's TS types as of React 18.
        fetchpriority="high"
        decoding="async"
        // Explicit center crop — object-center is the default but naming it
        // makes intent clear when a mobile portrait aspect box crops a
        // landscape source (the sides get shaved, the middle wins).
        className="absolute inset-0 h-full w-full object-cover object-center"
      />

      {/* Bottom-anchored gradient veil — grounds the type in the image
          instead of stamping a card on top. Kept to the lower half so the
          subway rider (upper right of frame) stays legible. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent"
      />

      {/* Copy overlay — bottom-left third, roughly where a movie poster
          would carry its title. Padded generously; no card chrome. */}
      <div className="absolute inset-x-0 bottom-0 p-6 md:p-10 lg:p-12">
        <div className="max-w-xl">
          <h1
            className="text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white"
            style={{ textShadow: '0 2px 12px rgba(0, 0, 0, 0.5)' }}
          >
            Your own recordings, on the way home.
          </h1>
          <p
            className="mt-3 md:mt-4 text-sm md:text-base leading-relaxed text-white/80 max-w-md"
            style={{ textShadow: '0 1px 8px rgba(0, 0, 0, 0.5)' }}
          >
            Scratches, synth jams, piano practice, voice memos — the stuff you
            make, ready when you want to hear it again.
          </p>

          {/* Primary CTA — anon-only surface (HeroPersonal only mounts when
              !isAuthenticated per Home.tsx), so the auth check is upstream.
              Copy motivates the sign-in with the outcome ("start your
              collection") instead of a bare "Sign in" — same voice as the
              headline's use-case framing. Matches Header's Soundwave button
              treatment but scaled up for hero prominence. */}
          <button
            type="button"
            onClick={signIn}
            className="mt-5 md:mt-6 inline-flex items-center gap-2 px-5 py-3 md:px-6 md:py-3.5 rounded-xl bg-soundwave-500 hover:bg-soundwave-600 active:scale-[0.97] text-white text-sm md:text-base font-semibold shadow-lg hover:shadow-xl transition-all"
          >
            <LogIn className="w-4 h-4 md:w-5 md:h-5" />
            Sign in to start your collection
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeroPersonal;
